import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Button, Form, Table, Modal, Badge, Alert, Dropdown, ButtonGroup, InputGroup, OverlayTrigger, Tooltip, ListGroup, Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import Pagination from '../../../../Pagination';
import { PageLoader } from '../../../../../Components/NoDataFoundTable/NoDataFoundTable';
import DatasetViewerToolbar from './Toolbar';
import DatasetViewerFilterPanel from './FilterPanel';
import DatasetViewerContextMenu from './ContextMenu';
import DatasetViewerModals from './Modals';
import DatasetViewerTableView from './TableView';

const LABELS = {
  STUDYID: "Study Identifier", USUBJID: "Unique Subject Identifier",
  SUBJID: "Subject Identifier", SITEID: "Study Site Identifier",
  DOMAIN: "Domain Abbreviation", AESEQ: "Sequence Number", AESPID: "Sponsor-Defined Identifier",
  SAFFL: "Safety Population Flag", SAFFN: "Safety Population Flag (N)",
  AGE: "Age", SEX: "Sex", RACE: "Race", ARM: "Description of Planned Arm",
  TRT01A: "Actual Treatment for Period 01",
  AETERM: "Reported Term for the Adverse Event", AEDECOD: "Dictionary-Derived Term",
  AESEV: "Severity/Intensity", AESER: "Serious Event", AEREL: "Causality",
  AEOUT: "Outcome of Adverse Event",
  VISIT: "Visit Name", VISITNUM: "Visit Number", PARAM: "Parameter",
  PARAMCD: "Parameter Code", AVAL: "Analysis Value", AVALU: "Analysis Value Unit",
  ADT: "Analysis Date", ANL01FL: "Analysis Flag 01"
};

const NUMERIC = new Set(["AESEQ", "AESPID", "SAFFN", "AGE", "VISITNUM", "AVAL"]);
const DATE = new Set(["ADT"]);

const buildDataset = () => {
  const study = "NCS-1080-25-CS";
  const site = "NCS";
  const subs = ["02", "01", "04", "02", "09R1", "01", "11", "11", "11", "13"];
  const arms = ["Placebo", "NCS-1080 Low", "NCS-1080 High"];
  const sexes = ["F", "M"];
  const races = ["WHITE", "BLACK OR AFRICAN AMERICAN", "ASIAN"];
  const aeTerms = [
    ["Headache", "Headache", "MILD"],
    ["Nausea", "Nausea", "MODERATE"],
    ["Fatigue", "Fatigue", "MILD"],
    ["Dizziness", "Dizziness", "MILD"],
    ["Rash", "Rash maculo-papular", "MODERATE"],
    ["Pyrexia", "Pyrexia", "MILD"],
    ["Vomiting", "Vomiting", "SEVERE"],
    ["Diarrhoea", "Diarrhoea", "MODERATE"],
    ["Cough", "Cough", "MILD"],
    ["Insomnia", "Insomnia", "MILD"]
  ];
  const rels = ["RELATED", "NOT RELATED", "POSSIBLY RELATED"];
  const outs = ["RECOVERED/RESOLVED", "RECOVERING/RESOLVING", "NOT RECOVERED/NOT RESOLVED"];
  const params = [
    ["Diastolic Blood Pressure (mmHg)", "DIABP", "mmHg", 60, 95],
    ["Systolic Blood Pressure (mmHg)", "SYSBP", "mmHg", 100, 150],
    ["Pulse Rate (beats/min)", "PULSE", "beats/min", 55, 100],
    ["Body Temperature (C)", "TEMP", "C", 36.1, 38.2]
  ];
  const visits = [["Baseline", 1], ["Week 2", 2], ["Week 4", 4], ["Week 8", 8]];
  const rows = [];
  let n = 0;
  for (let i = 0; i < 41; i++) {
    const subj = subs[i % subs.length];
    const usubj = `${study}-${site}-${subj}`;
    const arm = arms[i % arms.length];
    const ae = aeTerms[i % aeTerms.length];
    const pr = params[i % params.length];
    const vs = visits[i % visits.length];
    const seq = (i % 3) + 1;
    const span = pr[4] - pr[3];
    let val = pr[3] + span * ((Math.sin(n * 1.3) + 1) / 2);
    val = pr[3] < 40 ? +val.toFixed(1) : Math.round(val);
    const anl = (n % 6 === 0) ? "N" : "Y";
    rows.push({
      fieldRow: {
        STUDYID: study, USUBJID: usubj, SUBJID: subj, SITEID: site,
        DOMAIN: "AE", AESEQ: seq, AESPID: seq,
        SAFFL: "Y", SAFFN: 1, AGE: 34 + (i % 45), SEX: sexes[i % 2],
        RACE: races[i % races.length], ARM: arm, TRT01A: arm,
        AETERM: ae[0], AEDECOD: ae[1], AESEV: ae[2],
        AESER: (ae[2] === "SEVERE" ? "Y" : "N"),
        AEREL: rels[i % rels.length], AEOUT: outs[i % outs.length],
        VISIT: vs[0], VISITNUM: vs[1], PARAM: pr[0], PARAMCD: pr[1],
        AVAL: val, AVALU: pr[2],
        ADT: "2026-0" + (1 + (i % 6)) + "-" + String(5 + (i % 20)).padStart(2, "0"),
        ANL01FL: anl
      }
    });
    n++;
  }
  return rows;
};

const DATASET = buildDataset();

const esc = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;")
  .replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

const highlight = (text, q) => {
  if (!q) return esc(text);
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return esc(text);
  return esc(text.slice(0, i)) + '<span class="bg-warning text-dark rounded px-1">' + esc(text.slice(i, i + q.length)) + '</span>' + esc(text.slice(i + q.length));
};

const cmp = (a, b, col) => {
  const ea = (a == null || a === ""), eb = (b == null || b === "");
  if (ea && eb) return 0;
  if (ea) return -1;
  if (eb) return 1;
  if (NUMERIC.has(col)) return parseFloat(a) - parseFloat(b);
  if (DATE.has(col)) return Date.parse(a) - Date.parse(b);
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
};

const colType = (c) => NUMERIC.has(c) ? "numeric" : (DATE.has(c) ? "date" : "character");

const colFilterLabel = (f) => {
  const m = {
    contains: "contains", eq: "=", ne: "≠", starts: "starts", ends: "ends",
    gt: ">", lt: "<", between: "between", before: "before", after: "after",
    dbetween: "between", blank: "is blank", nonblank: "is not blank"
  };
  if (f.op === "blank" || f.op === "nonblank") return m[f.op];
  if (f.op === "between" || f.op === "dbetween") return `${m[f.op]} ${f.val}..${f.val2}`;
  return `${m[f.op] || f.op} ${f.val}`;
}

const DatasetViewer = ({
  data = DATASET,
  columns,
  hiddenColumns,
  startIndex = 0,
  onRowClick,
  onCellClick,
  showSrColumn = true,
  srColumnLabel = "Sr.",
  isFullScreen = false,
  setIsFullScreen = () => { },
  sorts = [],
  handleSort = () => { },
  clearSorts = () => { },
  quick = "",
  setQuick = () => { },
  showLabels = false,
  setShowLabels = () => { },
  toggle = () => { },
  setToggle = () => { },
  exportCSVFile = () => { },
  exportExcelFile = () => { },
  exportJSONFile = () => { },
  setShowFilterPanel,
  showFilterPanel,
  setShowColsPanel,
  showColsPanel,
  resetKey = 0,
  csvLoader = false,
  excelLoader = false,
  JSONLoader = false,
  studyPermission = {},
  totalPage,
  pageNumber,
  itemsPerPage,
  totalItems,
  setPageNumber,
  setItemsPerPage
}) => {

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [filter, setFilter] = useState("");
  const [filterErr, setFilterErr] = useState("");
  const [hidden, setHidden] = useState(() => {
    const initialHidden = new Set();
    Object.keys(hiddenColumns).forEach(key => {
      if (hiddenColumns[key]) initialHidden.add(key);
    });
    return initialHidden;
  });
  const [colFilters, setColFilters] = useState({});
  const [widths, setWidths] = useState({});
  const [order, setOrder] = useState(columns.map((_, i) => i));

  const [colSearch, setColSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [showColFilterModal, setShowColFilterModal] = useState(false);
  const [colFilterTarget, setColFilterTarget] = useState(null);
  const [colFilterOp, setColFilterOp] = useState("contains");
  const [colFilterVal, setColFilterVal] = useState("");
  const [colFilterVal2, setColFilterVal2] = useState("");
  const [contextMenu, setContextMenu] = useState(null);
  const [toast, setToast] = useState(null);
  const [showSetWidthModal, setShowSetWidthModal] = useState(false);
  const [widthTarget, setWidthTarget] = useState(null);
  const [frozen, setFrozen] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const [resizingCol, setResizingCol] = useState(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartW, setResizeStartW] = useState(0);

  const tableRef = useRef(null);

  const [processing, setProcessing] = useState(false);
  const processingTimer = useRef(null);
  const triggerProcessing = (ms = 200) => {
    if (processingTimer.current) clearTimeout(processingTimer.current);
    setProcessing(true);
    processingTimer.current = setTimeout(() => {
      setProcessing(false);
      processingTimer.current = null;
    }, ms);
  };

  const visibleColumns = useMemo(() =>
    columns.filter(c => !hidden.has(c)),
    [columns, hidden]
  );

  const visibleIndices = useMemo(() =>
    order.filter(i => !hidden.has(columns[i])),
    [order, columns, hidden]
  );

  const computed = useMemo(() => {
    let rows = [...data];
    let filterActive = false;
    let filterErrMsg = "";

    if (quick.trim()) {
      const q = quick.toLowerCase();
      rows = rows.filter(row =>
        visibleColumns.some(col =>
          String(row.fieldRow[col] == null ? "" : row.fieldRow[col]).toLowerCase().includes(q)
        )
      );
    }

    if (filter.trim()) {
      const expr = filter.trim();
      try {
        const tokens = expr.split(/\s+(and|or)\s+/i).filter(Boolean);
        const clauses = [];
        const connectors = [];
        for (let i = 0; i < tokens.length; i++) {
          if (i % 2 === 0) clauses.push(tokens[i].trim());
          else connectors.push(tokens[i].toLowerCase());
        }

        const evalClause = (clause, row) => {
          const m = clause.match(/^\s*([A-Za-z0-9_]+)\s*(==|=|!=|>=|<=|>|<)\s*(.+)\s*$/);
          if (!m) {
            const f = clause.toLowerCase();
            return visibleColumns.some(col => String(row.fieldRow[col] == null ? "" : row.fieldRow[col]).toLowerCase().includes(f));
          }
          const colToken = m[1];
          const op = m[2] === '=' ? '==' : m[2];
          let rawVal = m[3].trim();
          if ((rawVal.startsWith('"') && rawVal.endsWith('"')) || (rawVal.startsWith("'") && rawVal.endsWith("'"))) {
            rawVal = rawVal.slice(1, -1);
          }
          const colName = columns.find(c => c.toLowerCase() === colToken.toLowerCase());
          if (!colName) {
            throw new Error(`Unknown column: ${colToken}`);
          }
          const cell = row.fieldRow[colName];
          const lhsNum = parseFloat(cell);
          const rhsNum = parseFloat(rawVal);
          const bothNumeric = !isNaN(lhsNum) && !isNaN(rhsNum);

          switch (op) {
            case '==':
              return bothNumeric ? lhsNum === rhsNum : String(cell == null ? '' : cell) === rawVal;
            case '!=':
              return bothNumeric ? lhsNum !== rhsNum : String(cell == null ? '' : cell) !== rawVal;
            case '>':
              return bothNumeric ? lhsNum > rhsNum : String(cell == null ? '' : cell) > rawVal;
            case '<':
              return bothNumeric ? lhsNum < rhsNum : String(cell == null ? '' : cell) < rawVal;
            case '>=':
              return bothNumeric ? lhsNum >= rhsNum : String(cell == null ? '' : cell) >= rawVal;
            case '<=':
              return bothNumeric ? lhsNum <= rhsNum : String(cell == null ? '' : cell) <= rawVal;
            default:
              return false;
          }
        };

        rows = rows.filter(row => {
          const results = clauses.map(c => {
            try {
              return !!evalClause(c, row);
            } catch (e) {
              throw e;
            }
          });
          let res = results[0];
          for (let i = 0; i < connectors.length; i++) {
            const op = connectors[i];
            if (op === 'and') res = res && results[i + 1];
            else if (op === 'or') res = res || results[i + 1];
            else res = res && results[i + 1];
          }
          return res;
        });
        filterActive = true;
      } catch (e) {
        filterErrMsg = e.message || 'Invalid filter expression';
      }
    }

    const colF = Object.entries(colFilters).filter(([k, f]) =>
      f && (f.op === "blank" || f.op === "nonblank" || String(f.val || "").length || String(f.val2 || "").length)
    );
    if (colF.length) {
      filterActive = true;
      rows = rows.filter(row =>
        colF.every(([k, f]) => {
          const col = columns[+k];
          const raw = row.fieldRow[col];
          const s = (raw == null ? "" : String(raw));
          const blank = (s.trim() === "");
          if (f.op === "blank") return blank;
          if (f.op === "nonblank") return !blank;
          const val = String(f.val == null ? "" : f.val);
          const sl = s.toLowerCase();
          const vl = val.toLowerCase();
          switch (f.op) {
            case "contains": return sl.includes(vl);
            case "eq": return sl === vl;
            case "ne": return sl !== vl;
            case "starts": return sl.startsWith(vl);
            case "ends": return sl.endsWith(vl);
            case "gt": return parseFloat(s) > parseFloat(val);
            case "lt": return parseFloat(s) < parseFloat(val);
            case "between": {
              const v2 = parseFloat(f.val2);
              const n = parseFloat(s);
              return n >= parseFloat(val) && n <= v2;
            }
            default: return true;
          }
        })
      );
    }

    if (sorts.length) {
      rows.sort((a, b) => {
        for (const s of sorts) {
          const col = columns[s.col];
          const valA = a.fieldRow[col];
          const valB = b.fieldRow[col];
          const c = cmp(valA, valB, col);
          if (c) return c * (s.dir === "desc" ? -1 : 1);
        }
        return 0;
      });
    }

    return { rows, filterActive, colF, filterErr: filterErrMsg };
  }, [data, visibleColumns, quick, filter, colFilters, sorts, columns]);

  const { rows, filterActive, colF, filterErr: computedFilterErr } = computed;

  const total = data.length;
  const shown = rows.length;
  const allPage = pageSize === "all";
  const size = allPage ? (shown || 1) : pageSize;
  const pageCount = Math.max(1, Math.ceil(shown / size));
  const currentPage = Math.min(page, pageCount);
  const start = allPage ? 0 : (currentPage - 1) * size;
  const pageRows = allPage ? rows : rows.slice(start, start + size);

  const from = shown ? (allPage ? 1 : start + 1) : 0;
  const to = allPage ? shown : Math.min(start + size, shown);

  useEffect(() => {
    triggerProcessing(180);
    return () => {
      if (processingTimer.current) {
        clearTimeout(processingTimer.current);
        processingTimer.current = null;
      }
    };
  }, [quick, filter, JSON.stringify(colFilters), JSON.stringify(sorts), page, pageSize, JSON.stringify(order), Array.from(hidden).join(',')]);

  const getWidth = (col) => {
    const idx = columns.indexOf(col);
    return widths[idx] || 150;
  };

  const setColumnWidth = (col, w) => {
    const idx = columns.indexOf(col);
    setWidths(prev => ({ ...prev, [idx]: w }));
  };

  const autoFit = (col) => {
    const idx = columns.indexOf(col);
    let max = col.length;
    const lim = Math.min(data.length, 500);
    for (let i = 0; i < lim; i++) {
      const s = String(data[i].fieldRow[col] == null ? "" : data[i].fieldRow[col]);
      if (s.length > max) max = s.length;
    }
    setWidths(prev => ({ ...prev, [idx]: Math.min(420, Math.max(70, max * 7.4 + 34)) }));
  };

  const autoFitAll = () => {
    const newWidths = { ...widths };
    visibleColumns.forEach(col => {
      const idx = columns.indexOf(col);
      let max = col.length;
      const lim = Math.min(data.length, 500);
      for (let i = 0; i < lim; i++) {
        const s = String(data[i].fieldRow[col] == null ? "" : data[i].fieldRow[col]);
        if (s.length > max) max = s.length;
      }
      newWidths[idx] = Math.min(420, Math.max(70, max * 7.4 + 34));
    });
    setWidths(newWidths);
  };

  const resetWidth = (col) => {
    const idx = columns.indexOf(col);
    const newWidths = { ...widths };
    delete newWidths[idx];
    setWidths(newWidths);
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 1500);
  };

  const copyText = (text, msg) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => showToast(msg || "Copied"));
    } else {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch (e) { }
      ta.remove();
      showToast(msg || "Copied");
    }
  };

  const firstSort = sorts[0];

  const toggleColVis = (col) => {
    const newHidden = new Set(hidden);
    if (newHidden.has(col)) {
      newHidden.delete(col);
    } else {
      if (visibleColumns.length <= 1) {
        showToast("At least one column must remain visible.");
        return;
      }
      newHidden.add(col);
    }
    setHidden(newHidden);
  };

  const showAllCols = () => setHidden(new Set());

  const hideAllCols = () => {
    if (columns.length <= 1) {
      showToast("At least one column must remain visible.");
      return;
    }
    setHidden(new Set(columns.slice(1)));
  };

  const clearColFilter = (ci) => {
    const newColFilters = { ...colFilters };
    delete newColFilters[ci];
    setColFilters(newColFilters);
    setPage(1);
  };

  const clearAllFilters = () => {
    setFilter("");
    setFilterErr("");
    setColFilters({});
    setQuick("");
    setPage(1);
  };

  const handleColFilterApply = () => {
    const ci = colFilterTarget;
    if (ci === null) return;
    const op = colFilterOp;
    const newColFilters = { ...colFilters };
    if (op === "blank" || op === "nonblank") {
      newColFilters[ci] = { op };
    } else {
      newColFilters[ci] = { op, val: colFilterVal, val2: colFilterVal2 };
    }
    setColFilters(newColFilters);
    setPage(1);
    setShowColFilterModal(false);
  };

  const openColFilter = (col) => {
    const ci = columns.indexOf(col);
    const cur = colFilters[ci] || { op: "contains", val: "", val2: "" };
    setColFilterTarget(ci);
    setColFilterOp(cur.op || "contains");
    setColFilterVal(cur.val || "");
    setColFilterVal2(cur.val2 || "");
    setShowColFilterModal(true);
  };

  const filterByValue = (row, col) => {
    closeContextMenu();
    const val = String(row.fieldRow[col] == null ? "" : row.fieldRow[col]);
    const ci = columns.indexOf(col);
    setColFilters(prev => ({ ...prev, [ci]: { op: "eq", val } }));
    setShowFilterPanel(true);
    setPage(1);
  };

  const moveCol = (ci, where) => {
    const newOrder = [...order];
    const pos = newOrder.indexOf(ci);
    if (pos < 0) return;
    newOrder.splice(pos, 1);
    if (where === "left") newOrder.splice(Math.max(0, pos - 1), 0, ci);
    else if (where === "right") newOrder.splice(Math.min(newOrder.length, pos + 1), 0, ci);
    else if (where === "first") newOrder.unshift(ci);
    else if (where === "last") newOrder.push(ci);
    setOrder(newOrder);
  };

  const freezeTo = (col) => {
    const ci = columns.indexOf(col);
    const pos = order.filter(i => !hidden.has(columns[i])).indexOf(ci);
    if (pos >= 0) setFrozen(pos + 1);
  };

  const unfreezeAll = () => setFrozen(0);

  const openContextMenu = (e, items) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, items });
  };

  const closeContextMenu = () => setContextMenu(null);

  const showGenericModal = (title, body, onPrimary, primaryLabel = "OK") => {
    setModalContent({ title, body, onPrimary, primaryLabel });
    setShowModal(true);
  };

  const showRowDetails = (row, rowIndex) => {
    closeContextMenu();
    const body = (
      <Table className="m-0 custom-table" hover striped>
        <tbody>
          {visibleColumns.map(col => (
            <tr key={col}>
              <td style={{ whiteSpace: "nowrap", verticalAlign: "top", fontWeight: 600 }}>{col}</td>
              <td>{esc(row.fieldRow[col])}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
    showGenericModal(
      `Row Details — record ${rowIndex + 1}`,
      body,
      () => copyText(visibleColumns.map(col => `${col}\t${row.fieldRow[col]}`).join("\n"), "Row details copied"),
      "Copy"
    );
  };

  const showViewFullValue = (row, col) => {
    closeContextMenu();
    const val = String(row.fieldRow[col] == null ? "" : row.fieldRow[col]);
    const body = (
      <>
        <div className="text-muted small">{LABELS[col] || col} · type {colType(col)}</div>
        <Form.Control
          as="textarea"
          value={val}
          readOnly
          style={{ height: "110px", fontFamily: "monospace", resize: "vertical" }}
        />
      </>
    );
    showGenericModal(
      `${col} — Full Value`,
      body,
      () => copyText(val, "Value copied"),
      "Copy"
    );
  };

  const showColProps = (col) => {
    closeContextMenu();
    const ty = colType(col);
    let present = 0, missing = 0;
    const uniq = new Set();
    let min = null, max = null;
    for (const row of data) {
      const raw = row.fieldRow[col];
      if (raw == null || String(raw).trim() === "") { missing++; continue; }
      present++;
      uniq.add(String(raw));
      if (ty === "numeric") {
        const n = parseFloat(raw);
        if (!isNaN(n)) { if (min == null || n < min) min = n; if (max == null || n > max) max = n; }
      } else if (ty === "date") {
        const n = Date.parse(raw);
        if (!isNaN(n)) { if (min == null || n < min) min = n; if (max == null || n > max) max = n; }
      } else {
        if (min == null || String(raw) < min) min = String(raw);
        if (max == null || String(raw) > max) max = String(raw);
      }
    }
    let minD = min, maxD = max;
    if (ty === "date" && min != null) {
      minD = new Date(min).toISOString().slice(0, 10);
      maxD = new Date(max).toISOString().slice(0, 10);
    }

    const rowT = (k, v) => (
      <tr key={k}>
        <th style={{ textAlign: "left", color: "var(--bs-secondary)", fontWeight: 600, padding: "4px 14px 4px 0", whiteSpace: "nowrap" }}>{k}</th>
        <td style={{ padding: "4px 0" }}>{esc(v)}</td>
      </tr>
    );

    const body = (
      <Table size="sm" style={{ margin: 0 }}>
        <tbody>
          {rowT("Variable", col)}
          {rowT("Label", LABELS[col] || col)}
          {rowT("Type", ty)}
          {rowT("Position", columns.indexOf(col) + 1 + " of " + columns.length)}
          {rowT("Non-missing", present)}
          {rowT("Missing", missing)}
          {rowT("Unique values", uniq.size)}
          {rowT("Minimum", minD == null ? "—" : minD)}
          {rowT("Maximum", maxD == null ? "—" : maxD)}
        </tbody>
      </Table>
    );
    showGenericModal(`Column Properties — ${col}`, body, null, "Close");
  };

  const openSetWidth = (col) => {
    closeContextMenu();
    setWidthTarget(col);
    setShowSetWidthModal(true);
  };

  const copyCell = (row, col) => {
    closeContextMenu();
    copyText(String(row.fieldRow[col] == null ? "" : row.fieldRow[col]), "Cell copied");
  };

  const copyRow = (row) => {
    closeContextMenu();
    copyText(visibleColumns.map(col => row.fieldRow[col]).join("\t"), "Row copied");
  };

  const copyColumn = (col) => {
    closeContextMenu();
    const values = rows.map(r => r.fieldRow[col]);
    copyText(values.join("\n"), `Column ${col} copied`);
  };

  const download = (content, name, mime) => {
    const blob = new Blob([content], { type: mime || "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1500);
  };

  const exportData = (fmt) => {
    const cols = visibleColumns;
    if (fmt === "json") {
      const exportRows = rows.map(r => {
        const obj = {};
        cols.forEach(col => obj[col] = r.fieldRow[col]);
        return obj;
      });
      download(JSON.stringify(exportRows, null, 2), "dataset.json", "application/json");
    } else if (fmt === "xlsx") {
      const th = cols.map(c => `<th>${esc(c)}</th>`).join("");
      const tr = rows.map(r => `<tr>${cols.map(c => `<td>${esc(r.fieldRow[c])}</td>`).join("")}</tr>`).join("");
      const htmlDoc = `<html><head><meta charset="utf-8"></head><body><table border="1">` +
        `<thead><tr>${th}</tr></thead><tbody>${tr}</tbody></table></body></html>`;
      download(htmlDoc, "dataset.xls", "application/vnd.ms-excel");
    } else {
      const q = (s) => {
        s = String(s == null ? "" : s);
        return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
      };
      const lines = [cols.map(q).join(",")].concat(rows.map(r => cols.map(c => q(r.fieldRow[c])).join(",")));
      download(lines.join("\n"), "dataset.csv", "text/csv;charset=utf-8");
    }
  };

  const reset = () => {
    setPage(1);
    setPageSize(100);
    setQuick("");
    setFilter("");
    setFilterErr("");
    setHidden(new Set(Object.keys(hiddenColumns).filter(k => hiddenColumns[k])));
    setColFilters({});
    setWidths({});
    setOrder(columns.map((_, i) => i));
    setShowLabels(false);
    setShowFilterPanel(false);
    setShowColsPanel(false);
    setColSearch("");
    setFrozen(0);
    setSelectedRow(null);
  };

  useEffect(() => {
    reset();
  }, [resetKey]);

  const getHeaderMenuItems = (col, ci) => [
    { label: "Sort Ascending", icon: "▲", action: () => { handleSort(ci, false, "asc"); closeContextMenu(); } },
    { label: "Sort Descending", icon: "▼", action: () => { handleSort(ci, false, "desc"); closeContextMenu(); } },
    { label: "Clear Sort", icon: "✕", action: () => { clearSorts(); closeContextMenu(); } },
    { label: "Filter Column…", icon: "▼", action: () => { openColFilter(col); closeContextMenu(); } },
    {
      label: "Hide Column", icon: "⊘", action: () => {
        const newHidden = new Set(hidden);
        if (visibleColumns.length <= 1) {
          showToast("At least one column must remain visible.");
          closeContextMenu();
          return;
        }
        newHidden.add(col);
        setHidden(newHidden);
        closeContextMenu();
      }
    },
    { label: "Auto Fit Column", icon: "↔", action: () => { autoFit(col); closeContextMenu(); } },
    { label: "Auto Fit All Columns", icon: "↔", action: () => { autoFitAll(); closeContextMenu(); } },
    { label: "Set Column Width…", icon: "▭", action: () => { openSetWidth(col); closeContextMenu(); } },
    { label: "Reset Column Width", icon: "⟲", action: () => { resetWidth(col); closeContextMenu(); } },
    { label: "Freeze Up To Here", icon: "❄", action: () => { freezeTo(col); closeContextMenu(); } },
    { label: "Unfreeze All", icon: "✷", action: () => { unfreezeAll(); closeContextMenu(); } },
    { label: "Move Left", icon: "⇤", action: () => { moveCol(ci, "left"); closeContextMenu(); } },
    { label: "Move Right", icon: "⇥", action: () => { moveCol(ci, "right"); closeContextMenu(); } },
    { label: "Move to First", icon: "⏮", action: () => { moveCol(ci, "first"); closeContextMenu(); } },
    { label: "Move to Last", icon: "⏭", action: () => { moveCol(ci, "last"); closeContextMenu(); } },
    { label: "Copy Column", icon: "⧉", action: () => { copyColumn(col); closeContextMenu(); } },
    { label: "Column Properties…", icon: "ⓘ", action: () => { showColProps(col); closeContextMenu(); } },
  ];

  const getCellMenuItems = (row, col) => [
    { label: "Copy Cell", icon: "⧉", action: () => copyCell(row, col) },
    { label: "Copy Row", icon: "☰", action: () => copyRow(row) },
    { label: "Copy Column", icon: "▥", action: () => copyColumn(col) },
    { label: "View Full Value…", icon: "🔍", action: () => showViewFullValue(row, col) },
    { label: "Filter by This Value", icon: "▼", action: () => filterByValue(row, col) },
    { label: "Clear Filter", icon: "✕", action: () => { const ci = columns.indexOf(col); clearColFilter(ci); closeContextMenu(); } },
    { label: "View Row Details…", icon: "ⓘ", action: () => showRowDetails(row, data.indexOf(row)) },
  ];

  const onResizeMouseDown = (e, col) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = getWidth(col);
    const onMouseMove = (ev) => {
      const w = Math.max(60, startW + (ev.clientX - startX));
      setColumnWidth(col, w);
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeContextMenu();
        if (isFullScreen) setIsFullScreen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isFullScreen]);

  useEffect(() => {
    const handleClick = () => closeContextMenu();
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  useEffect(() => {
    if (!showFilterPanel) {
      setFilter("");
      setFilterErr("");
      setPage(1);
    }
  }, [showFilterPanel]);

  return (
    <div
      className={`d-flex flex-column ${isFullScreen ? "full-screen-component" : ""}`}
    >
      <DatasetViewerToolbar
        isFullScreen={isFullScreen}
        toggle={toggle}
        exportCSVFile={exportCSVFile}
        exportExcelFile={exportExcelFile}
        exportJSONFile={exportJSONFile}
        csvLoader={csvLoader}
        excelLoader={excelLoader}
        JSONLoader={JSONLoader}
        studyPermission={studyPermission}
        setIsFullScreen={setIsFullScreen}
      />
      <DatasetViewerFilterPanel
        showFilterPanel={showFilterPanel}
        filter={filter}
        setFilter={setFilter}
        setFilterErr={setFilterErr}
        setPage={setPage}
        computedFilterErr={computedFilterErr}
        colFilters={colFilters}
        columns={columns}
        clearColFilter={clearColFilter}
        filterActive={filterActive}
        shown={shown}
      />
      <div className={isFullScreen ? "overflow-auto" : ""}>
        <DatasetViewerTableView
          processing={processing}
          tableRef={tableRef}
          isFullScreen={isFullScreen}
          showSrColumn={showSrColumn}
          srColumnLabel={srColumnLabel}
          visibleColumns={visibleColumns}
          columns={columns}
          sorts={sorts}
          handleSort={handleSort}
          openContextMenu={openContextMenu}
          getHeaderMenuItems={getHeaderMenuItems}
          getWidth={getWidth}
          showLabels={showLabels}
          colType={colType}
          LABELS={LABELS}
          pageRows={pageRows}
          startIndex={startIndex}
          data={data}
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          onRowClick={onRowClick}
          onCellClick={onCellClick}
          getCellMenuItems={getCellMenuItems}
          onResizeMouseDown={onResizeMouseDown}
          autoFit={autoFit}
          frozen={frozen}
          clearColFilter={clearColFilter}
          visibleColumnsLength={visibleColumns.length + (showSrColumn ? 1 : 0)}
          esc={esc}
          highlight={highlight}
          showRowDetails={showRowDetails}
        />
      </div>
      {!processing &&
        <div className="position-sticky bottom-0 start-0 bg-white border-top">
          <Pagination
            totalPage={totalPage}
            pageNumber={pageNumber}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            setPageNumber={setPageNumber}
            setItemsPerPage={setItemsPerPage}
          />
        </div>
      }
      <DatasetViewerContextMenu
        contextMenu={contextMenu}
        closeContextMenu={closeContextMenu}
      />

      <DatasetViewerModals
        showModal={showModal}
        modalContent={modalContent}
        setShowModal={setShowModal}
        showColFilterModal={showColFilterModal}
        setShowColFilterModal={setShowColFilterModal}
        colFilterTarget={colFilterTarget}
        columns={columns}
        colFilterOp={colFilterOp}
        setColFilterOp={setColFilterOp}
        colFilterVal={colFilterVal}
        setColFilterVal={setColFilterVal}
        colFilterVal2={colFilterVal2}
        setColFilterVal2={setColFilterVal2}
        handleColFilterApply={handleColFilterApply}
        showSetWidthModal={showSetWidthModal}
        setShowSetWidthModal={setShowSetWidthModal}
        widthTarget={widthTarget}
        getWidth={getWidth}
        setColumnWidth={setColumnWidth}
      />

      {toast && (
        <div
          className="position-fixed start-50 translate-middle-x bg-dark text-white rounded px-3 py-2"
          style={{ bottom: "22px", zIndex: 2000 }}
        >
          {toast}
        </div>
      )}
    </div>
  );
};

export default DatasetViewer;